/**
 * Belote's deck, ranking, card points, legality and visibility.
 */
import { SUITS, cardId, rankOf, suitOf } from '../../core/cards.js';
import { currentWinner } from '../../core/ranking.js';
import { anyOf } from '../../core/legality.js';
import { sameTeam } from '../../core/seats.js';
import { emptyTrick } from '../../core/trick.js';
/** 7 8 9 10 J Q K A in every suit (rules §1.1). Canonical order is fixed
 *  forever: the fairness commitment hashes it. */
export function beloteDeck(_config) {
    const cards = [];
    for (const suit of SUITS)
        for (let r = 7; r <= 14; r++)
            cards.push(cardId(r, suit));
    return Object.freeze(cards);
}
/**
 * Rules §1.4–1.5. Classic deals 3 then 2 and turns the next card up — the
 * kitty, which everybody sees — leaving eleven in the stock for the
 * completion deal after a take. Coinche deals all eight up front.
 */
export function beloteDealPlan(config) {
    return config.variant === 'classic'
        ? { steps: [{ each: 3 }, { each: 2 }, { kitty: 1 }], firstSeatOffset: 1 }
        : { steps: [{ each: 3 }, { each: 2 }, { each: 3 }], firstSeatOffset: 1 };
}
// Rules §2.1–2.2: trump J 9 A 10 K Q 8 7; plain A 10 K Q J 9 8 7.
const TRUMP_ORDER = [11, 9, 14, 10, 13, 12, 8, 7];
const PLAIN_ORDER = [14, 10, 13, 12, 11, 9, 8, 7];
const TRUMP_POINTS = { 11: 20, 9: 14, 14: 11, 10: 10, 13: 4, 12: 3 };
const PLAIN_POINTS = { 14: 11, 10: 10, 13: 4, 12: 3, 11: 2 };
export function beloteRanking(trump) {
    return {
        trumpSuit: trump,
        effectiveSuit: (card) => suitOf(card),
        isTrump: (card) => trump !== null && suitOf(card) === trump,
        strength: (card) => {
            const order = trump !== null && suitOf(card) === trump ? TRUMP_ORDER : PLAIN_ORDER;
            return order.length - order.indexOf(rankOf(card) ?? 0);
        },
    };
}
/** Card points: 62 in the trump suit, 30 in each other, 152 in all. */
export function belotePoints(card, trump) {
    const table = trump !== null && suitOf(card) === trump ? TRUMP_POINTS : PLAIN_POINTS;
    return table[rankOf(card) ?? 0] ?? 0;
}
export function trumpOfHand(hand) {
    return hand.contract?.trump ?? null;
}
export function beloteCurrentTrick(hand) {
    return hand.tricks[hand.tricks.length - 1] ?? emptyTrick(0, hand.dealer);
}
/**
 * The legal-move tree of rules §6.2, verbatim in structure, for the second,
 * third and fourth players to a trick:
 *
 *  - trump led: follow, and overtrump if you can;
 *  - plain suit led and held: follow (no need to beat);
 *  - void, partner winning: anything;
 *  - void, an opponent winning: trump, overtrumping if you can; unable to
 *    overtrump, play a losing trump anyway — unless the pissette is allowed,
 *    when any card will do.
 */
export function beloteLegalPlays(holding, seat, trick, ctx, table, config) {
    if (trick.plays.length === 0 || trick.ledSuit === null)
        return anyOf(holding);
    const led = trick.ledSuit;
    const follow = holding.filter((c) => ctx.effectiveSuit(c) === led);
    const trumps = holding.filter((c) => ctx.isTrump(c));
    const trumpsOnTable = trick.plays.filter((p) => ctx.isTrump(p.card));
    const topTrump = trumpsOnTable.length === 0
        ? -1
        : Math.max(...trumpsOnTable.map((p) => ctx.strength(p.card)));
    const over = (cards) => cards.filter((c) => ctx.strength(c) > topTrump);
    if (led === ctx.trumpSuit) {
        if (follow.length === 0)
            return anyOf(holding);
        const beaters = over(follow);
        return anyOf(beaters.length > 0 ? beaters : follow);
    }
    if (follow.length > 0)
        return anyOf(follow);
    const winning = currentWinner(trick, ctx);
    if (winning && winning.seat !== seat && sameTeam(table, winning.seat, seat))
        return anyOf(holding);
    if (trumps.length === 0)
        return anyOf(holding);
    const beaters = over(trumps);
    if (beaters.length > 0)
        return anyOf(beaters);
    return anyOf(config.allowPissette ? holding : trumps);
}
function handOver(hand, matchFinished) {
    return matchFinished || hand.phase === 'handComplete' || hand.phase === 'abandoned';
}
/** Your own hand, everyone's at the end; the turned card is face up for all
 *  (it is the kitty until someone takes it); declarations shown after trick 1
 *  are public through `extra`, which `revealed` owns up to. */
export const beloteVisibility = {
    holdings(hand, viewerSeat, matchFinished) {
        const over = handOver(hand, matchFinished);
        return hand.secret.holdings.map((_, seat) => (over || seat === viewerSeat ? 'cards' : 'count'));
    },
    kitty: () => 'cards',
    discards: () => 'hidden',
    revealed(hand) {
        const extra = hand.extra;
        return (extra?.declarations ?? []).flatMap((d) => d.cards);
    },
};
//# sourceMappingURL=rules.js.map